import {
  ALL_STAKEHOLDER_TYPES_ORDER,
  RESPONDER_STAKEHOLDER_TYPES,
  normalizeStakeholderCode,
} from '@buxus/shared'
import type { ScreenInteractionContext } from './platform/analytics/interactionAnalytics'
import { trackEmailPressed, trackPhonePressed, trackUrlPressed } from './platform/analytics/interactionAnalytics'

const CONTACT_URI = 'https://www.Buxus.io'
const CONTACT_CONDENSED_URI = 'www.Buxus.io'
const CONTACT_PHONE = '844-888-3318'
const HELP_EMAIL = 'Help@Buxus.io'

type Props = {
  stakeholderCode: string | null
  profileStakeholderRaw: string | null
  availableStakeholderCodes: string[]
  onRequestUnlistedOperator?: () => void
  analytics?: ScreenInteractionContext
}

export function InformationScreen({
  stakeholderCode,
  profileStakeholderRaw,
  availableStakeholderCodes,
  onRequestUnlistedOperator,
  analytics,
}: Props) {
  const effectiveCode =
    stakeholderCode ??
    normalizeStakeholderCode(profileStakeholderRaw, availableStakeholderCodes)
  const effectiveCanon =
    (effectiveCode &&
      normalizeStakeholderCode(effectiveCode, ALL_STAKEHOLDER_TYPES_ORDER)) ||
    effectiveCode
  const isResponder =
    typeof effectiveCanon === 'string' &&
    (RESPONDER_STAKEHOLDER_TYPES as readonly string[]).includes(effectiveCanon)

  return (
    <section className="app-information">
      <h2 className="app-information-section-title">Getting the most from Buxus</h2>
      <p className="app-information-lead">
        For the best user experience, please take the following steps:
      </p>
      <ul className="app-information-list">
        <li>
          Stay signed into Buxus. This will permit you accessing information even when you do not have a
          cell signal or access to WiFi. Failing to do so may limit your ability to connect during critical
          times.
        </li>
        <li>
          Remember to refresh the data every 30 days or so. Simply go to the “Download” tab, click “Refresh”
          and then “Download all” again to ensure that you have the most current data on your device.
        </li>
      </ul>
      <p className="app-information-warning">
        <strong>WARNING:</strong> Never use your mobile device or create any other source of ignition in the
        area of a suspected pipeline leak. Products transported by pipelines may be highly-flammable or
        explosive. Always move away from a suspected leak on foot in an upwind or crosswind direction to a
        safe location before attempting to use your mobile device.
      </p>
      {isResponder ? <p className="app-information-note">All pipelines are not pictured.</p> : null}

      <h2 className="app-information-section-title">Contact Buxus</h2>
      <p className="app-information-contact">
        Need help, have questions or suggestions, contact us at{' '}
        <a
          href={`tel:${CONTACT_PHONE}`}
          onClick={analytics ? () => trackPhonePressed({ ...analytics, phone: CONTACT_PHONE }) : undefined}
        >
          {CONTACT_PHONE}
        </a>{' '}
        or{' '}
        <a
          href={`mailto:${HELP_EMAIL}`}
          onClick={
            analytics ? () => trackEmailPressed({ ...analytics, email: HELP_EMAIL }) : undefined
          }
        >
          {HELP_EMAIL}
        </a>
        . You can also sign up for a Buxus tutorial for you
        or your organization by visiting{' '}
        <a
          href={CONTACT_URI}
          target="_blank"
          rel="noreferrer"
          onClick={
            analytics
              ? () => trackUrlPressed({ ...analytics, url: CONTACT_URI, title: CONTACT_CONDENSED_URI })
              : undefined
          }
        >
          {CONTACT_CONDENSED_URI}
        </a>{' '}
        and clicking on the red bullseye.
      </p>

      {onRequestUnlistedOperator ? (
        <div className="app-information-unlisted">
          <h3 className="app-information-subsection-title">Can't find your operator?</h3>
          <p className="app-information-unlisted-prompt">
            Request an operator that is not listed in Buxus and our team will follow up.
          </p>
          <button type="button" className="app-link-btn" onClick={onRequestUnlistedOperator}>
            Request an Unlisted Operator
          </button>
        </div>
      ) : null}
    </section>
  )
}
